import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

interface UseQueryStateOptions {
  defaultValue?: string | string[]
  parse?: (value: string) => any
  serialize?: (value: any) => string
}

type SetValue = (value: any) => void

export function useQueryState(key: string, options?: UseQueryStateOptions): [any, SetValue] {
  const [searchParams, setSearchParams] = useSearchParams()
  const { defaultValue, parse, serialize } = options || {}

  // Get current value from URL
  const rawValue = searchParams.get(key)

  let value: any
  if (rawValue === null) {
    value = defaultValue ?? null
  } else if (parse) {
    value = parse(rawValue)
  } else {
    value = rawValue
  }

  const setValue = useCallback<SetValue>(
    (newValue) => {
      // Always get fresh params from the URL instead of using stale closure
      const currentUrl = new URL(window.location.href)
      const freshParams = new URLSearchParams(currentUrl.search)

      // Create new params from fresh params
      const newSearchParams = new URLSearchParams(freshParams)

      if (
        newValue === null ||
        newValue === undefined ||
        (Array.isArray(newValue) && newValue.length === 0) ||
        newValue === ''
      ) {
        // Remove the param when setting to null/undefined/empty
        newSearchParams.delete(key)
      } else {
        let stringValue: string
        if (serialize) {
          stringValue = serialize(newValue)
        } else if (Array.isArray(newValue)) {
          stringValue = newValue.join(',')
        } else {
          stringValue = String(newValue)
        }

        newSearchParams.set(key, stringValue)
      }

      setSearchParams(newSearchParams, { replace: true })
    },
    [key, setSearchParams, serialize],
  )

  return [value, setValue]
}
